import {
  isNewSessionDraftId,
  NEW_SESSION_DRAFT_ID_PREFIX,
} from "@/components/codesmith/constants";

// Composer draft persistence, shared by AgentChatInput (which saves/restores
// the text and attachments under its `sessionId`) and the Codesmith sidebar
// (which lists `draft:<uuid>` entries as client-side draft conversations).
//
// Two kinds of drafts live under the same localStorage prefix:
// - `agent-chat-draft:<uuid>`: follow-up text typed into an existing
//   session's composer. Short-lived; restored only when the user returns to
//   that session soon after.
// - `agent-chat-draft:draft:<uuid>`: a new-session draft entity. These are
//   first-class client-side objects: they render as rows in the session
//   sidebar, so they persist much longer and broadcast a change event that
//   keeps the sidebar in sync while the user types.

const DRAFT_KEY_PREFIX = "agent-chat-draft:";

/** A composer attachment serialized into a draft's localStorage entry. */
export interface DraftAttachment {
  fileName: string;
  mediaType: string;
  /** Full `data:` URI of the file contents. */
  dataUrl: string;
}

/**
 * Per-attachment budget for the serialized `dataUrl` (UTF-16 code units).
 * A single Retina screenshot PNG can exceed the whole localStorage quota in
 * Safari/Firefox (~5-10 MB), so oversized payloads persist as metadata-only.
 */
export const MAX_PERSISTED_ATTACHMENT_CHARS = 2 * 1024 * 1024;

/** Combined `dataUrl` budget for one draft entry (UTF-16 code units). */
export const MAX_PERSISTED_ATTACHMENTS_TOTAL_CHARS = 4 * 1024 * 1024;

interface StoredDraft {
  text: string;
  timestamp: number;
  attachments?: DraftAttachment[];
  /** `GithubAppInstallation` PK (`blacksmith_id`) the draft was composed
   * under. Absent on entries saved before drafts were installation-scoped;
   * those list under every org until an edit stamps them. */
  installationModelId?: number;
}

function readStoredDraft(sessionId: string): StoredDraft | null {
  try {
    const raw = localStorage.getItem(`${DRAFT_KEY_PREFIX}${sessionId}`);
    if (!raw) return null;
    return JSON.parse(raw) as StoredDraft;
  } catch {
    return null;
  }
}

/** The localStorage key a composer sessionId's draft text persists under.
 * Exposed so cross-tab `storage` event listeners can match on it. */
export function draftStorageKey(sessionId: string): string {
  return `${DRAFT_KEY_PREFIX}${sessionId}`;
}

const SESSION_DRAFT_TTL_MS = 60 * 60 * 1000;
const NEW_SESSION_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Window event fired whenever a new-session draft entity changes. */
export const NEW_SESSION_DRAFTS_CHANGED_EVENT = "codesmith-drafts-changed";

/** Mint a fresh new-session draft entity id (`draft:<uuid>`). */
export function newSessionDraftId(): string {
  const uuid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}`;
  return `${NEW_SESSION_DRAFT_ID_PREFIX}${uuid}`;
}

function draftTtlMs(sessionId: string): number {
  return isNewSessionDraftId(sessionId)
    ? NEW_SESSION_DRAFT_TTL_MS
    : SESSION_DRAFT_TTL_MS;
}

function notifyNewSessionDraftsChanged(sessionId: string) {
  if (!isNewSessionDraftId(sessionId)) return;
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(NEW_SESSION_DRAFTS_CHANGED_EVENT));
}

// Debounced draft saves — write to localStorage at most once every 500ms per
// composer sessionId. Timers are keyed by sessionId so canceling one draft's
// pending save (e.g. deleting a sidebar draft row) never drops text still in
// flight for another composer. Each pending entry carries the text it will
// write, and getDraft prefers it over storage, so a composer that remounts
// (or a cross-tab sync racing a local save) reads the freshest typed text
// instead of silently dropping the last ≤500ms of typing.
const pendingDraftSaves = new Map<
  string,
  { timer: ReturnType<typeof setTimeout>; text: string }
>();

export function saveDraftDebounced(
  sessionId: string,
  text: string,
  installationModelId?: number | null,
) {
  const pending = pendingDraftSaves.get(sessionId);
  if (pending) clearTimeout(pending.timer);
  pendingDraftSaves.set(sessionId, {
    text,
    timer: setTimeout(() => {
      pendingDraftSaves.delete(sessionId);
      saveDraft(sessionId, text, undefined, installationModelId);
    }, 500),
  });
}

export function cancelDraftTimer(sessionId: string) {
  const pending = pendingDraftSaves.get(sessionId);
  if (pending) {
    clearTimeout(pending.timer);
    pendingDraftSaves.delete(sessionId);
  }
}

/** Whether a debounced save is still in flight for this composer. */
export function hasPendingDraftSave(sessionId: string): boolean {
  return pendingDraftSaves.has(sessionId);
}

export function getDraft(sessionId: string): string {
  const pending = pendingDraftSaves.get(sessionId);
  if (pending) return pending.text;
  try {
    const stored = readStoredDraft(sessionId);
    if (!stored) return "";
    if (Date.now() - stored.timestamp > draftTtlMs(sessionId)) {
      localStorage.removeItem(`${DRAFT_KEY_PREFIX}${sessionId}`);
      return "";
    }
    return stored.text;
  } catch {
    return "";
  }
}

/** Attachments persisted with the draft, if any (TTL respected). */
export function getDraftAttachments(sessionId: string): DraftAttachment[] {
  try {
    const stored = readStoredDraft(sessionId);
    if (!stored) return [];
    if (Date.now() - stored.timestamp > draftTtlMs(sessionId)) return [];
    return stored.attachments ?? [];
  } catch {
    return [];
  }
}

/** Remove every draft entry past its TTL (and malformed ones) to reclaim
 * quota hoarded by old 7-day new-session drafts before retrying a write. */
function pruneExpiredDraftEntries() {
  try {
    const expired: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(DRAFT_KEY_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const sessionId = key.slice(DRAFT_KEY_PREFIX.length);
      try {
        const { timestamp } = JSON.parse(raw) as StoredDraft;
        if (Date.now() - timestamp > draftTtlMs(sessionId)) expired.push(key);
      } catch {
        expired.push(key);
      }
    }
    for (const key of expired) localStorage.removeItem(key);
  } catch {}
}

function writeDraftEntry(key: string, entry: StoredDraft): boolean {
  const serialized = JSON.stringify(entry);
  try {
    localStorage.setItem(key, serialized);
    return true;
  } catch {
    pruneExpiredDraftEntries();
    try {
      localStorage.setItem(key, serialized);
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Persist a draft's text. Attachments default to whatever the entry already
 * holds, so the debounced text path can't clobber an attachment save (and
 * vice versa). The entry is removed only when both text and attachments are
 * empty.
 *
 * Returns whether the full intended entry landed. On quota failure the entry
 * is rewritten without attachments (keeping the text) or removed outright, so
 * storage never keeps an attachment list the caller no longer has; a later
 * hydration adopting that stale list would clobber the user's in-memory
 * files.
 */
export function saveDraft(
  sessionId: string,
  text: string,
  attachments?: DraftAttachment[],
  installationModelId?: number | null,
): boolean {
  let saved = false;
  try {
    const key = `${DRAFT_KEY_PREFIX}${sessionId}`;
    const stored = readStoredDraft(sessionId);
    const kept = attachments ?? stored?.attachments ?? [];
    const installation = installationModelId ?? stored?.installationModelId;
    if (!text && kept.length === 0) {
      localStorage.removeItem(key);
      saved = true;
    } else {
      const entry: StoredDraft = { text, timestamp: Date.now() };
      if (kept.length > 0) entry.attachments = kept;
      if (installation != null) entry.installationModelId = installation;
      saved = writeDraftEntry(key, entry);
      if (!saved) {
        try {
          if (text) {
            const fallback: StoredDraft = { text, timestamp: Date.now() };
            if (installation != null)
              fallback.installationModelId = installation;
            localStorage.setItem(key, JSON.stringify(fallback));
          } else {
            localStorage.removeItem(key);
          }
        } catch {
          try {
            localStorage.removeItem(key);
          } catch {}
        }
      }
    }
    notifyNewSessionDraftsChanged(sessionId);
  } catch {
    return false;
  }
  return saved;
}

/**
 * Attachments over the per-attachment cap, or overflowing the per-entry
 * budget, are skipped: they stay in memory only, so a reload loses just the
 * oversized payloads instead of blowing the quota and losing everything.
 */
function capPersistedAttachments(
  attachments: DraftAttachment[],
): DraftAttachment[] {
  const kept: DraftAttachment[] = [];
  let total = 0;
  for (const att of attachments) {
    if (att.dataUrl.length > MAX_PERSISTED_ATTACHMENT_CHARS) continue;
    if (total + att.dataUrl.length > MAX_PERSISTED_ATTACHMENTS_TOTAL_CHARS)
      continue;
    total += att.dataUrl.length;
    kept.push(att);
  }
  return kept;
}

/**
 * Persist a draft's attachments, keeping its stored text. No-ops when the
 * attachments already match what's stored, so a composer mount that merely
 * re-serializes its restored files doesn't bump the draft's edit time.
 * Returns whether the persisted entry reflects these attachments (with
 * oversized payloads capped away).
 */
export function saveDraftAttachments(
  sessionId: string,
  attachments: DraftAttachment[],
  installationModelId?: number | null,
): boolean {
  const stored = readStoredDraft(sessionId);
  const existing = stored?.attachments ?? [];
  const capped = capPersistedAttachments(attachments);
  const unchanged =
    existing.length === capped.length &&
    existing.every(
      (att, i) =>
        att.dataUrl === capped[i].dataUrl &&
        att.fileName === capped[i].fileName &&
        att.mediaType === capped[i].mediaType,
    );
  if (unchanged) return true;
  return saveDraft(sessionId, stored?.text ?? "", capped, installationModelId);
}

/** Remove a draft entry outright (e.g. after sending) and notify listeners. */
export function clearDraft(sessionId: string) {
  cancelDraftTimer(sessionId);
  try {
    localStorage.removeItem(`${DRAFT_KEY_PREFIX}${sessionId}`);
  } catch {}
  notifyNewSessionDraftsChanged(sessionId);
}

/** Attachment metadata surfaced on sidebar draft rows (no file data). */
export interface NewSessionDraftAttachment {
  fileName: string;
  mediaType: string;
}

/** A client-side new-session draft entity, listed in the session sidebar. */
export interface NewSessionDraft {
  /** Full draft entity id (`draft:<uuid>`), usable as a composer sessionId. */
  id: string;
  text: string;
  /** Epoch ms of the last saved edit. */
  updatedAt: number;
  attachments?: NewSessionDraftAttachment[];
  /** See StoredDraft.installationModelId. */
  installationModelId?: number;
}

/**
 * Persisted new-session draft entities, most recently edited first. When
 * `installationModelId` is given, only drafts composed under that
 * installation (plus unstamped pre-scoping drafts) are returned; entries for
 * other installations stay in storage untouched. Expired entries are pruned
 * as a side effect.
 */
export function listNewSessionDrafts(
  installationModelId?: number | null,
): NewSessionDraft[] {
  if (typeof window === "undefined") return [];
  const drafts: NewSessionDraft[] = [];
  try {
    const entityPrefix = `${DRAFT_KEY_PREFIX}${NEW_SESSION_DRAFT_ID_PREFIX}`;
    const expired: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(entityPrefix)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const {
          text,
          timestamp,
          attachments,
          installationModelId: draftInstallation,
        } = JSON.parse(raw) as StoredDraft;
        const empty = !text && (attachments?.length ?? 0) === 0;
        if (empty || Date.now() - timestamp > NEW_SESSION_DRAFT_TTL_MS) {
          expired.push(key);
          continue;
        }
        if (
          installationModelId != null &&
          draftInstallation != null &&
          draftInstallation !== installationModelId
        ) {
          continue;
        }
        drafts.push({
          id: key.slice(DRAFT_KEY_PREFIX.length),
          text,
          updatedAt: timestamp,
          attachments: attachments?.map(({ fileName, mediaType }) => ({
            fileName,
            mediaType,
          })),
          installationModelId: draftInstallation,
        });
      } catch {
        expired.push(key);
      }
    }
    for (const key of expired) localStorage.removeItem(key);
  } catch {
    return [];
  }
  return drafts.sort((a, b) => b.updatedAt - a.updatedAt);
}

/** Remove a new-session draft entity and notify listeners. */
export function deleteNewSessionDraft(draftId: string) {
  // clearDraft cancels any pending debounced save, which could otherwise
  // resurrect the entity; dropping that save loses at most 500ms of typing,
  // which matches the debounce contract. Timers for other sessions are
  // untouched.
  clearDraft(draftId);
}

/**
 * Converts the legacy single global new-session draft (saved under the bare
 * `draft` id) into a `draft:<uuid>` entity so it shows up in the sidebar
 * instead of silently squatting on a key nothing reads anymore.
 */
export function migrateLegacyNewSessionDraft() {
  try {
    const legacyKey = `${DRAFT_KEY_PREFIX}draft`;
    const raw = localStorage.getItem(legacyKey);
    if (!raw) return;
    localStorage.removeItem(legacyKey);
    const { text, timestamp } = JSON.parse(raw) as {
      text: string;
      timestamp: number;
    };
    // The legacy draft used the short session TTL; honor it one last time.
    if (!text || Date.now() - timestamp > SESSION_DRAFT_TTL_MS) return;
    localStorage.setItem(
      `${DRAFT_KEY_PREFIX}${newSessionDraftId()}`,
      JSON.stringify({ text, timestamp }),
    );
  } catch {}
}
